import React from "react";
import { Link, useParams } from "react-router-dom";
import { DEFAULT_PRODUCTS, BRAND, waLink } from "@/config/site.config";
import { resolveImg } from "@/lib/img";
import { ArrowLeft, MessageCircle, ArrowRight, Check } from "lucide-react";

export default function ProductDetailPage() {
  const { id } = useParams();
  const product = DEFAULT_PRODUCTS.find((p) => p.id === id) || {
    id,
    name: id?.replace(/-/g, " ").replace(/\b\w/g, (s) => s.toUpperCase()),
    description: "A bespoke AIBoxHub product. Contact us to learn more.",
    image: "",
    tag: "Product",
    price_from: 49,
    cta: "Get Started",
    link: "/register",
  };

  const features = [
    "Multi-tenant, secure architecture",
    "AI-powered automation built in",
    "Mobile-first dashboards",
    "Custom integrations available",
    "24/7 concierge support",
  ];

  return (
    <div className="min-h-screen container-px py-28" data-testid={`product-detail-${product.id}`}>
      <div className="mx-auto max-w-6xl">
        <Link to="/products" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm">
          <ArrowLeft className="h-4 w-4" /> All products
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="eyebrow text-gold-300">{product.tag || "Product"}</div>
            <h1 className="mt-2 font-serif text-4xl md:text-6xl text-white leading-[1.05]">
              {product.name.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="gold-text italic">{product.name.split(" ").slice(-1)}</span>
            </h1>
            <p className="mt-5 text-lg text-white/65 leading-relaxed max-w-xl">{product.description}</p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register" className="pill pill-gold" data-testid="product-start-btn">
                Start free demo <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={waLink(`Hi%20AIBoxHub%2C%20I%20want%20to%20learn%20about%20${encodeURIComponent(product.name)}`)}
                 target="_blank" rel="noopener noreferrer"
                 className="pill pill-ghost"
                 data-testid="product-whatsapp">
                <MessageCircle className="h-4 w-4" /> WhatsApp us
              </a>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-white/75">
                  <Check className="h-4 w-4 text-gold-400 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-white/10 bg-ink-200">
              {product.image
                ? <img src={resolveImg(product.image)} alt={product.name} className="aspect-[4/3] w-full object-cover" />
                : <div className="aspect-[4/3] w-full bg-gradient-to-br from-gold-500/10 via-ink-200 to-ink-300" />}
            </div>
            <div className="mt-4 glass-card p-5">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40">Starting at</div>
              <div className="font-serif text-3xl gold-text">${product.price_from || 49}<span className="text-base text-white/55"> /mo</span></div>
              <div className="mt-2 text-xs text-white/50">Annual billing available. Cancel anytime.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
